// The user's camera during a coach call. One still becomes a habit photo
// check-in. The stream stays off until they, or the coach, turn it on, and
// the call host stops it when the call ends.

type CamState = { on: boolean; error: 'denied' | 'unavailable' | null }

const OFF: CamState = { on: false, error: null }
const listeners = new Set<() => void>()
let state: CamState = OFF
let stream: MediaStream | null = null
let video: HTMLVideoElement | null = null
let starting: Promise<boolean> | null = null
// The circles on screen. The still is taken from one of these, because a
// video that is not in the page often stays black on a phone.
const previews = new Set<HTMLVideoElement>()

export type FrameCandidate = { videoWidth: number; shown: boolean; kind: 'preview' | 'capture' }

export function pickFrameCandidate<T extends FrameCandidate>(items: T[]): T | null {
  return (
    items.find((item) => item.kind === 'preview' && item.shown && item.videoWidth > 0) ??
    items.find((item) => item.kind === 'capture' && item.videoWidth > 0) ??
    items.find((item) => item.videoWidth > 0) ??
    null
  )
}

export function registerSelfPreview(el: HTMLVideoElement) {
  previews.add(el)
  return () => void previews.delete(el)
}

function emit() {
  for (const listener of listeners) listener()
}

export function subscribeSelfCamera(cb: () => void) {
  listeners.add(cb)
  return () => void listeners.delete(cb)
}

export function getSelfCamera() {
  return state
}

export function selfCameraStream() {
  return stream
}

export function stopSelfCamera() {
  stream?.getTracks().forEach((track) => track.stop())
  stream = null
  if (video) {
    video.srcObject = null
    video.remove()
    video = null
  }
  if (state.on || state.error) {
    state = OFF
    emit()
  }
}

async function start(): Promise<boolean> {
  if (stream) return true
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    state = { on: false, error: 'unavailable' }
    emit()
    return false
  }
  try {
    // Prefer the front camera so the coach sees the user's face on a phone.
    // A laptop with one camera still opens that camera.
    stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: { ideal: 'user' },
        width: { ideal: 720 },
        height: { ideal: 720 },
      },
    })
    video = document.createElement('video')
    video.muted = true
    video.playsInline = true
    video.setAttribute('playsinline', 'true')
    video.autoplay = true
    // In the page, or mobile browsers never decode a frame. Not display:none.
    video.setAttribute('aria-hidden', 'true')
    video.style.cssText =
      'position:fixed;left:0;bottom:0;width:16px;height:16px;opacity:0;pointer-events:none'
    document.body.appendChild(video)
    video.srcObject = stream
    await video.play().catch(() => {})
    state = { on: true, error: null }
    emit()
    return true
  } catch (err) {
    stream?.getTracks().forEach((track) => track.stop())
    stream = null
    video?.remove()
    video = null
    const denied =
      err instanceof DOMException &&
      (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError')
    state = { on: false, error: denied ? 'denied' : 'unavailable' }
    emit()
    return false
  }
}

export function setSelfCamera(on: boolean): Promise<boolean> {
  if (!on) {
    stopSelfCamera()
    return Promise.resolve(true)
  }
  if (stream) return Promise.resolve(true)
  if (starting) return starting
  starting = start().finally(() => {
    starting = null
  })
  return starting
}

function shown(el: HTMLVideoElement) {
  return el.isConnected && el.getClientRects().length > 0
}

function frameCandidates() {
  return [
    ...[...previews].map((el) => ({
      el,
      videoWidth: el.videoWidth,
      shown: shown(el),
      kind: 'preview' as const,
    })),
    ...(video
      ? [{ el: video, videoWidth: video.videoWidth, shown: true, kind: 'capture' as const }]
      : []),
  ]
}

async function waitForFrame() {
  const started = Date.now()
  let picked = pickFrameCandidate(frameCandidates())
  while (!picked) {
    if (Date.now() - started > 2000) return null
    await new Promise((resolve) => setTimeout(resolve, 80))
    picked = pickFrameCandidate(frameCandidates())
  }
  return picked.el
}

function drawFrame(el: HTMLVideoElement) {
  const side = Math.min(el.videoWidth, el.videoHeight)
  const canvas = document.createElement('canvas')
  const size = 640
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  ctx?.drawImage(
    el,
    (el.videoWidth - side) / 2,
    (el.videoHeight - side) / 2,
    side,
    side,
    0,
    0,
    size,
    size,
  )
  return { canvas, ctx }
}

// A phone can report a size before the first real frame, which is black.
function blank(ctx: CanvasRenderingContext2D | null, size: number) {
  if (!ctx) return true
  try {
    const data = ctx.getImageData(0, 0, size, size).data
    let lit = 0
    for (let i = 0; i < data.length; i += 64) {
      if (data[i] + data[i + 1] + data[i + 2] > 24) lit++
    }
    return lit < 8
  } catch {
    return false
  }
}

export async function grabSelfFrame(): Promise<Blob | null> {
  const el = await waitForFrame()
  if (!el) return null
  let drawn = drawFrame(el)
  if (blank(drawn.ctx, 640)) {
    await new Promise((resolve) => setTimeout(resolve, 180))
    const again = (await waitForFrame()) ?? el
    drawn = drawFrame(again)
    if (blank(drawn.ctx, 640)) return null
  }
  return new Promise((resolve) => drawn.canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.82))
}

export type FrameEffect = { id: string; label: string; prompt: string }

type FrameAsk = {
  title: string
  url: string
  effects: FrameEffect[]
  appliedId: string | null
  styling: boolean
  styleError: boolean
  generation: number
  apply: (effect: FrameEffect) => void
  revert: () => void
  resolve: (yes: boolean) => void
}
const askListeners = new Set<() => void>()
let ask: FrameAsk | null = null
let askGeneration = 0

function emitAsk() {
  for (const listener of askListeners) listener()
}

export function subscribeFrameAsk(cb: () => void) {
  askListeners.add(cb)
  return () => askListeners.delete(cb)
}

export function getFrameAsk() {
  return ask
}

export function dismissFrameAsk() {
  ask?.resolve(false)
}

export function currentFrameAskGeneration() {
  return ask?.generation ?? null
}

export function patchFrameAsk(
  patch: Partial<Pick<FrameAsk, 'url' | 'effects' | 'appliedId' | 'styling' | 'styleError'>>,
) {
  if (!ask) return
  ask = { ...ask, ...patch }
  emitAsk()
}

const ASK_MS = 60_000

export function requestFrameAsk(
  title: string,
  url: string,
  hooks?: { apply?: (effect: FrameEffect) => void; revert?: () => void },
): Promise<boolean> {
  dismissFrameAsk()
  return new Promise((resolve) => {
    let timer = 0
    const finish = (yes: boolean) => {
      if (ask?.resolve !== finish) return
      window.clearTimeout(timer)
      ask = null
      emitAsk()
      resolve(yes)
    }
    timer = window.setTimeout(() => finish(false), ASK_MS)
    ask = {
      title,
      url,
      effects: [],
      appliedId: null,
      styling: false,
      styleError: false,
      generation: ++askGeneration,
      apply: (effect) => hooks?.apply?.(effect),
      revert: () => hooks?.revert?.(),
      resolve: finish,
    }
    emitAsk()
  })
}
